import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Download, ArrowLeft, Loader2 } from "lucide-react";
import { api, API } from "@/lib/api";
import { useCart } from "@/state/CartContext";
import ShareButton from "@/components/ShareButton";

const POLL_INTERVAL = 2500;
const MAX_ATTEMPTS = 12;

const CITY_DATE = {
  sydney: "1 July 2026",
  melbourne: "Tue 11 Aug 2026",
  adelaide: "Sun 9 Aug 2026",
  london: "15 July 2026",
  paris: "17 July 2026",
};

// Map currency code → symbol for receipts shown on the success page.
// Falls back to "$" for AUD/USD-style currencies we haven't catalogued.
const CURRENCY_SYMBOL = { aud: "$", usd: "$", gbp: "£", eur: "€" };
const symbolFor = (code) => CURRENCY_SYMBOL[(code || "aud").toLowerCase()] || "$";

export default function Success() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "checking", payment_status: "pending" });
  const [error, setError] = useState(null);
  const attemptsRef = useRef(0);
  const stoppedRef = useRef(false);
  const cart = useCart();

  useEffect(() => {
    if (!sessionId) {
      setError("Missing session_id.");
      return;
    }

    const poll = async () => {
      if (stoppedRef.current) return;
      attemptsRef.current += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        setState(data);

        const isPaid = data.payment_status === "paid";
        const finalised =
          isPaid &&
          ((data.kind === "release" && data.download_token) ||
            (data.kind === "ticket" && data.ticket_reference) ||
            data.kind === "tip" ||
            (data.kind === "cart" &&
              (!data.has_wav || data.download_token) &&
              (!data.has_ticket || (data.ticket_references && data.ticket_references.length > 0))));

        if (finalised) {
          stoppedRef.current = true;
          if (data.download_token) {
            try { localStorage.setItem("metamorph_download_token", data.download_token); } catch (e) { /* ignore */ }
          }
          // Persist tickets list (single or multiple) into localStorage so the user can revisit
          try {
            const existing = JSON.parse(localStorage.getItem("metamorph_tickets") || "[]");
            const next = existing.filter((t) => t.session_id !== sessionId);
            if (data.kind === "ticket" && data.ticket_reference) {
              next.push({
                session_id: sessionId,
                ticket_reference: data.ticket_reference,
                city: data.product,
                city_label: data.ticket_city_label,
                date: data.ticket_date,
                quantity: data.quantity,
              });
            }
            if (data.kind === "cart" && Array.isArray(data.ticket_references)) {
              for (const r of data.ticket_references) {
                next.push({
                  session_id: sessionId,
                  ticket_reference: r.reference,
                  city: r.city,
                  city_label: r.label,
                  date: r.date,
                  quantity: r.quantity,
                });
              }
            }
            localStorage.setItem("metamorph_tickets", JSON.stringify(next));
          } catch (e) { /* ignore */ }
          // Clear the in-app cart after a successful purchase
          if (data.kind === "cart") {
            try { cart.clear(); } catch (e) { /* ignore */ }
          }
          return;
        }
        if (data.status === "expired") {
          stoppedRef.current = true;
          setError("Payment session expired.");
          return;
        }
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          stoppedRef.current = true;
          setError("Still processing. Please refresh in a moment.");
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      } catch (e) {
        stoppedRef.current = true;
        setError(e?.response?.data?.detail || "Could not verify payment.");
      }
    };
    poll();
    return () => {
      stoppedRef.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const kind = state?.kind || "release";
  const isCart = kind === "cart";
  const isTicket = kind === "ticket";
  const isTip = kind === "tip";
  const isRelease = kind === "release";
  const paid = state.payment_status === "paid";
  const downloadUrl = null; // WAV is not downloadable on-success — emailed on release
  const hasWav = (state.cart || []).some((it) => it.type === "wav");
  const ticketRefs = isCart ? (state.ticket_references || []) : [];

  return (
    <main className="relative min-h-[100svh] text-white flex flex-col px-6 md:px-12 py-8" data-testid="success-page">
      <div className="film-grain" aria-hidden />

      <header className="relative z-10 flex items-center justify-between mb-12 sm:mb-20">
        <Link to="/" className="vintage-link text-xs sm:text-sm uppercase" data-testid="success-back">
          ← Metamorph
        </Link>
        <span className="vintage-link text-[10px] sm:text-xs uppercase opacity-70 tracking-[0.18em]" data-testid="success-status-label">
          {paid ? (isCart ? "Order confirmed" : isTicket ? "Ticket" : isTip ? "Tip received" : "Receipt") : error ? "Paused" : "Verifying"}
        </span>
        <span className="opacity-0 text-xs">·</span>
      </header>

      <div className="relative z-10 flex-1 max-w-2xl mx-auto w-full">
        <h1
          className="vintage-text text-5xl sm:text-6xl md:text-7xl leading-[0.95] mb-12 text-center"
          data-testid="success-title"
        >
          {paid ? (isCart ? "Order in." : isTicket ? "You're in." : isTip ? "Thank you." : "It is yours.") : error ? "A pause." : "Verifying…"}
        </h1>

        {!paid && !error && (
          <div className="flex items-center justify-center gap-3 opacity-70" data-testid="success-loading">
            <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.2} />
            <span className="vintage-link text-[11px] uppercase tracking-[0.22em]">Verifying with Stripe…</span>
          </div>
        )}

        {/* ─── CART (multi-item) ─── */}
        {paid && isCart && (
          <div data-testid="success-cart" className="space-y-10">
            {/* Order summary */}
            <div className="border border-white/20 p-6 sm:p-8">
              <p className="vintage-link text-[10px] uppercase tracking-[0.22em] opacity-65 mb-4">Order</p>
              <ul className="divide-y divide-white/10" data-testid="success-cart-list">
                {(state.cart || []).map((it, idx) => (
                  <li key={idx} className="py-3 flex items-baseline justify-between gap-4">
                    <span>
                      <span className="vintage-text text-lg block leading-tight">{it.label}</span>
                      {it.quantity > 1 && (
                        <span className="font-serif italic text-xs text-white/45">qty {it.quantity}</span>
                      )}
                    </span>
                    <span className="font-mono tabular-nums text-sm">{symbolFor(state.currency)}{Number(it.subtotal).toFixed(2)}</span>
                  </li>
                ))}
              </ul>
              <div className="border-t border-white/15 mt-4 pt-3 flex justify-between vintage-text text-xl">
                <span>Total</span>
                <span className="font-mono tabular-nums" data-testid="success-total">{symbolFor(state.currency)}{Number(state.amount_total || 0).toFixed(2)} {(state.currency || "AUD").toUpperCase()}</span>
              </div>
              {(state.customer_name || state.customer_email) && (
                <div className="mt-4 text-xs text-white/55 grid grid-cols-2 gap-2" data-testid="success-customer">
                  {state.customer_name && <div><span className="opacity-60">Name · </span>{state.customer_name}</div>}
                  {state.customer_email && <div className="break-all"><span className="opacity-60">Email · </span>{state.customer_email}</div>}
                </div>
              )}
            </div>

            {/* WAV — emailed on release (no immediate download) */}
            {hasWav && (
              <div className="text-center" data-testid="success-wav-section">
                <p className="vintage-link text-[10px] uppercase tracking-[0.22em] opacity-65 mb-3">Metamorph Package</p>
                <p className="font-serif italic text-sm text-white/70 leading-relaxed">
                  A private download for Metamorph will be sent on release,<br className="hidden sm:block" />
                  prior to it being available publicly.
                </p>
                {state.customer_email && (
                  <p className="font-serif italic text-xs text-white/45 mt-3" data-testid="success-wav-email-note">
                    We have <span className="text-white/80">{state.customer_email}</span> on file.
                  </p>
                )}
              </div>
            )}

            {/* Tickets */}
            {ticketRefs.length > 0 && (
              <div data-testid="success-tickets-section">
                <p className="vintage-link text-[10px] uppercase tracking-[0.22em] opacity-65 mb-4">
                  {ticketRefs.length === 1 ? "Your ticket" : `Your tickets (${ticketRefs.length})`}
                </p>
                <div className="space-y-4">
                  {ticketRefs.map((r, idx) => (
                    <div key={idx} className="border border-white/20 p-5 sm:p-6" data-testid={`success-ticket-${r.city}`}>
                      <div className="flex items-baseline justify-between gap-4 mb-3">
                        <h3 className="vintage-text text-3xl leading-none">{r.label}</h3>
                        <span className="vintage-link text-[10px] uppercase opacity-80 tracking-[0.2em]">
                          {r.date || CITY_DATE[r.city] || ""}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-xs">
                        <div>
                          <p className="vintage-link text-[9px] uppercase tracking-[0.22em] opacity-65 mb-1">Reference</p>
                          <p className="font-mono text-sm tracking-wider">{r.reference}</p>
                        </div>
                        <div>
                          <p className="vintage-link text-[9px] uppercase tracking-[0.22em] opacity-65 mb-1">Admits</p>
                          <p className="vintage-text text-xl">{r.quantity}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="font-serif italic text-xs text-white/45 mt-4">
                  Bring your references to the door. Venue details will be emailed to {state.customer_email ? <span className="text-white/70">{state.customer_email}</span> : "you"} closer to the date.
                </p>
              </div>
            )}

            {/* Vinyl shipping confirmation */}
            {state.has_vinyl && state.shipping_details && (
              <div className="border border-white/15 p-5 sm:p-6" data-testid="success-vinyl-section">
                <p className="vintage-link text-[10px] uppercase tracking-[0.22em] opacity-65 mb-3">Vinyl · shipping to</p>
                <p className="font-serif text-sm leading-relaxed text-white/80">
                  {state.shipping_details.name}<br />
                  {state.shipping_details.line1}{state.shipping_details.line2 ? `, ${state.shipping_details.line2}` : ""}<br />
                  {state.shipping_details.city}{state.shipping_details.state ? `, ${state.shipping_details.state}` : ""} {state.shipping_details.postcode}<br />
                  {state.shipping_details.country}
                </p>
                <p className="font-serif italic text-xs text-white/45 mt-3">
                  We'll send a tracking number to {state.customer_email || "your email"} once it ships.
                </p>
              </div>
            )}

            <div className="flex gap-3 flex-wrap pt-2">
              <Link to="/store" className="btn-line" data-testid="success-store">
                Back to the store
              </Link>
            </div>

            {/* Share the moment */}
            <div className="pt-6 flex flex-col items-center gap-3 border-t border-white/10" data-testid="success-share-section">
              <ShareButton
                url={`${window.location.origin}/`}
                title="Metamorph by George Maple"
                text="I just picked up Metamorph by George Maple. Hold a piece of it."
                testid="success-share"
              />
            </div>
          </div>
        )}

        {/* ─── LEGACY single-kind paths (kept for backwards compatibility) ─── */}
        {paid && isTicket && (
          <div data-testid="success-ticket" className="space-y-10">
            <div className="border border-white/20 p-8 sm:p-10">
              <div className="flex items-baseline justify-between gap-4 mb-6">
                <h2 className="vintage-text text-4xl sm:text-5xl leading-none">{state.ticket_city_label || "—"}</h2>
                <span className="vintage-link text-[10px] uppercase opacity-80 tracking-[0.2em] text-right">{state.ticket_date || ""}</span>
              </div>
              <div className="grid grid-cols-2 gap-6 border-y border-white/15 py-6">
                <div>
                  <p className="vintage-link text-[10px] uppercase tracking-[0.22em] opacity-65 mb-2">Reference</p>
                  <p className="font-mono text-base sm:text-lg tracking-wider">{state.ticket_reference}</p>
                </div>
                <div>
                  <p className="vintage-link text-[10px] uppercase tracking-[0.22em] opacity-65 mb-2">Admits</p>
                  <p className="vintage-text text-2xl sm:text-3xl">{state.quantity ?? 1}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {paid && isRelease && (
          <div className="space-y-8 text-center" data-testid="success-release">
            <p className="font-serif italic text-base sm:text-lg text-white/60 max-w-lg mx-auto">
              The full release is below in lossless WAV. Headphones, low light, settle in.
            </p>
            {downloadUrl && (
              <a href={downloadUrl} download className="btn-line btn-filled" data-testid="success-download-legacy">
                Download WAV <Download className="w-4 h-4" strokeWidth={1} />
              </a>
            )}
          </div>
        )}

        {paid && isTip && (
          <div className="text-center space-y-8" data-testid="success-tip">
            <p className="font-serif italic text-base sm:text-lg text-white/60 max-w-lg mx-auto">
              Your support travels further than you think.
            </p>
            <Link to="/" className="btn-line" data-testid="success-home">
              <ArrowLeft className="w-4 h-4" strokeWidth={1} /> Back
            </Link>
          </div>
        )}

        {error && (
          <div className="text-center" data-testid="success-error">
            <p className="text-white/60 mb-8 max-w-lg mx-auto">{error}</p>
            <Link to="/" className="btn-line">
              <ArrowLeft className="w-4 h-4" strokeWidth={1} /> Back
            </Link>
          </div>
        )}
      </div>
    </main>
  );
}
